/**
 * Normalize the data-vintage year shown in map legends and data labels.
 *
 * Esri's "USA Demographics and Boundaries 2024" product carries per-variable
 * vintage years in its field aliases / layer titles (e.g. business/NAICS fields
 * are labeled 2023, the population forecast is "2023-2028"). We surface a single
 * current vintage (2024) across the app so map legends match the data tables,
 * which already normalize the same way.
 *
 * @param {string} text alias / title / legend string
 * @returns {string} the same string with 2021/2022/2023 rewritten to 2024
 */
export function normalizeLegendYear(text) {
  if (typeof text !== 'string' || !text) return text;
  return text
    .replace(/\b2021\b/g, '2024')
    .replace(/\b2022\b/g, '2024')
    .replace(/\b2023\b/g, '2024');
}

/**
 * Rewrite the legend-facing year on an Esri renderer in place-safe fashion.
 * Returns a renderer with normalized legendOptions titles. If nothing changes,
 * the original renderer is returned so callers can skip reassignment.
 *
 * @param {object} renderer an Esri renderer (class-breaks, simple, etc.)
 * @returns {object} renderer with normalized legend titles
 */
export function normalizeRendererLegendYear(renderer) {
  if (!renderer) return renderer;
  const clone = typeof renderer.clone === 'function' ? renderer.clone() : renderer;
  let changed = false;

  if (clone.legendOptions?.title) {
    const next = normalizeLegendYear(clone.legendOptions.title);
    if (next !== clone.legendOptions.title) {
      clone.legendOptions.title = next;
      changed = true;
    }
  }

  if (Array.isArray(clone.visualVariables)) {
    clone.visualVariables.forEach(vv => {
      if (vv?.legendOptions?.title) {
        const next = normalizeLegendYear(vv.legendOptions.title);
        if (next !== vv.legendOptions.title) {
          vv.legendOptions.title = next;
          changed = true;
        }
      }
    });
  }

  return changed ? clone : renderer;
}

/** The field a renderer colors by (direct field or first visual-variable field). */
function rendererField(renderer) {
  if (!renderer) return null;
  if (renderer.field) return renderer.field;
  if (Array.isArray(renderer.visualVariables)) {
    const vv = renderer.visualVariables.find(v => v?.field);
    if (vv) return vv.field;
  }
  return null;
}

/** Look up a field's alias on a layer via fieldsIndex or the fields array. */
function fieldAlias(layer, name) {
  if (!name) return null;
  const fromIndex = layer.fieldsIndex?.get?.(name);
  if (fromIndex?.alias) return fromIndex.alias;
  if (Array.isArray(layer.fields)) {
    const f = layer.fields.find(x => x?.name === name);
    if (f?.alias) return f.alias;
  }
  return null;
}

/**
 * Normalize every legend-facing year on a single Esri layer: its title, its
 * renderer legend options, and its field aliases. Recurses into MapImageLayer
 * sublayers. Mutates in place so the live Legend widget updates.
 *
 * Critically, the ArcGIS Legend widget derives the renderer subtitle from the
 * field alias when the renderer has no legendOptions.title (the case for our
 * authored demographic webmaps), but it does NOT reactively pick up alias
 * mutations. So when the colored field's alias carries a year, we set
 * renderer.legendOptions.title explicitly to the normalized alias -- that is
 * what actually flips the visible subtitle (e.g. "2023 Median Household Income
 * (Esri)" -> "2024 ..."). Aliases are still rewritten for popup consistency.
 *
 * @param {object} layer an Esri layer, sublayer, or group child
 */
function normalizeOneLayer(layer) {
  if (!layer) return;
  if (layer.title) layer.title = normalizeLegendYear(layer.title);

  // Force the renderer legend subtitle from the field alias. This MUST run
  // before we rewrite field aliases below: fieldsIndex and the fields array
  // share the same Field objects, so normalizing aliases first would leave
  // nothing for the "changed a year" guard to detect, and a plain alias
  // mutation does not update the Legend on its own.
  if (layer.renderer) {
    let renderer = normalizeRendererLegendYear(layer.renderer);
    const hasTitle = renderer.legendOptions?.title;
    if (!hasTitle) {
      const alias = fieldAlias(layer, rendererField(renderer));
      const title = normalizeLegendYear(alias);
      // Only force a title when normalization actually changed a year, so we
      // never alter legends whose alias has no vintage to rewrite.
      if (alias && title !== alias) {
        if (renderer === layer.renderer && typeof renderer.clone === 'function') {
          renderer = renderer.clone();
        }
        renderer.legendOptions = { ...(renderer.legendOptions || {}), title };
      }
    }
    if (renderer !== layer.renderer) layer.renderer = renderer;
  }

  // Rewrite field aliases afterwards for popup consistency (and as the Legend
  // fallback for any layer not handled above).
  if (Array.isArray(layer.fields)) {
    layer.fields.forEach(field => {
      if (field?.alias) field.alias = normalizeLegendYear(field.alias);
    });
  }

  if (layer.sublayers?.forEach) layer.sublayers.forEach(normalizeOneLayer);
}

/**
 * Walk an Esri layer collection (incl. GroupLayer children, flattened into
 * allLayers, and MapImageLayer sublayers) and normalize every legend-facing
 * year. Mutates layers in place so the live Legend widget updates.
 *
 * @param {object} layers an Esri Collection of layers (e.g. webmap.allLayers)
 */
export function normalizeLayersLegendYear(layers) {
  if (!layers?.forEach) return;
  layers.forEach(normalizeOneLayer);
}
